import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Copy, Code, Broadcast as Webhook } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";

const EVENTS = ["transfer.created", "deposit.approved", "lead.created"];

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [hookForm, setHookForm] = useState({ url: "", events: EVENTS });

  const load = async () => {
    const [k, h] = await Promise.all([api.get("/apikeys"), api.get("/webhooks")]);
    setKeys(k.data); setHooks(h.data);
  };
  useEffect(() => { load(); }, []);

  const genKey = async () => {
    const { data } = await api.post("/apikeys");
    setNewKey(data.api_key);
    toast.success("Chave gerada — copie agora, não aparecerá de novo!");
    load();
  };
  const delKey = async (id) => { await api.delete(`/apikeys/${id}`); load(); };
  const addHook = async () => {
    if (!hookForm.url) return toast.error("Informe uma URL");
    await api.post("/webhooks", hookForm);
    setHookForm({ url: "", events: EVENTS });
    toast.success("Webhook criado");
    load();
  };
  const delHook = async (id) => { await api.delete(`/webhooks/${id}`); load(); };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copiado"); };

  return (
    <div className="fadeUp">
      <div className="label-caps mb-2">Integrações · Pagamentos</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">API & Webhooks</h1>
      <p className="text-white/60 mb-10 max-w-2xl">Endpoints e eventos exclusivos para <b>recebimento de pagamentos</b>. Gere chaves para integrar sua conta Mercado Pago e configure URLs que serão notificadas em cada Pix aprovado, venda ou depósito.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-flat p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="label-caps">Chaves de API</div>
              <div className="font-display font-bold text-xl flex items-center gap-2"><Code size={20} /> Autenticação</div>
            </div>
            <button onClick={genKey} className="btn-primary text-xs flex items-center gap-1" data-testid="gen-apikey"><Plus size={14} /> Gerar chave</button>
          </div>

          {newKey && (
            <div className="border border-[var(--accent)] bg-[var(--accent)]/10 p-3 rounded-sm mb-4">
              <div className="label-caps mb-2 text-[var(--accent)]">Nova chave (copie agora)</div>
              <div className="flex gap-2 items-center">
                <code className="text-xs font-mono break-all flex-1" data-testid="new-apikey">{newKey}</code>
                <button onClick={() => copy(newKey)} className="btn-ghost text-xs px-2"><Copy size={12} /></button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {keys.length === 0 && <div className="text-white/40 text-sm">Nenhuma chave.</div>}
            {keys.map((k) => (
              <div key={k.id} className="flex justify-between items-center border border-[var(--border)] rounded-sm px-3 py-2" data-testid={`apikey-${k.id}`}>
                <code className="text-xs font-mono">{k.prefix}...</code>
                <button onClick={() => delKey(k.id)} className="text-[var(--danger)] hover:opacity-70"><Trash size={14} /></button>
              </div>
            ))}
          </div>

          <div className="mt-6 border-t border-[var(--border)] pt-4">
            <div className="label-caps mb-2">Exemplo de uso</div>
            <pre className="text-xs font-mono bg-[var(--bg-base)] border border-[var(--border)] p-3 rounded-sm overflow-x-auto">{`curl -X POST ${API}/transfer \\
  -H "Authorization: Bearer <API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"to_email":"cliente@x.com","amount":100}'`}</pre>
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="mb-4">
            <div className="label-caps">Webhooks</div>
            <div className="font-display font-bold text-xl flex items-center gap-2"><Webhook size={20} /> Eventos em tempo real</div>
          </div>

          <div className="space-y-3 mb-4">
            <input placeholder="https://seu-site.com/webhook" value={hookForm.url} onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })} data-testid="webhook-url" className="w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none" />
            <div className="flex flex-wrap gap-2">
              {EVENTS.map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-xs border border-[var(--border)] px-2 py-1 rounded-sm cursor-pointer">
                  <input type="checkbox" checked={hookForm.events.includes(ev)} onChange={() => {
                    setHookForm((h) => ({ ...h, events: h.events.includes(ev) ? h.events.filter(e => e !== ev) : [...h.events, ev] }));
                  }} className="accent-[var(--accent)]" />
                  <code>{ev}</code>
                </label>
              ))}
            </div>
            <button onClick={addHook} className="btn-primary w-full text-sm" data-testid="add-webhook">Adicionar webhook</button>
          </div>

          <div className="space-y-2">
            {hooks.length === 0 && <div className="text-white/40 text-sm">Nenhum webhook configurado.</div>}
            {hooks.map((h) => (
              <div key={h.id} className="border border-[var(--border)] rounded-sm p-3 text-xs" data-testid={`webhook-${h.id}`}>
                <div className="font-mono break-all">{h.url}</div>
                <div className="flex justify-between mt-2">
                  <div className="text-white/50">{h.events.join(", ")}</div>
                  <button onClick={() => delHook(h.id)} className="text-[var(--danger)]"><Trash size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
